import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import CenteredContainer from 'components/CenteredContainer';
import Info from '../Info';
import MainActions from '../MainActions';

import styles from './styles.module.scss';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import OrgEventsList from 'components/EventsList/OrgEventsList';
import TeamList from 'components/TeamList';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import OrgDirectory from '../../../associations/components/OrgDirectory';
import PillButton from '../PillButton';
import AssociatedWith from '../AssociatedWith';
import { OutgoingMembershipDto } from 'api/dtos/memberships/products';
import AssocEventsList from 'components/EventsList/AssocEventsList';

interface Props {
  toggleShareModal: () => void;
  join: () => void;
  coverUrl: string;
  badgeUrl: string;
  name: string;
  manageText: string;
  memberCount: number;
  memberPreview: {
    firstName: string;
    id: string;
    lastName: string;
    photoUrl?: string;
  }[];
  address: string;
  editLink?: string;
  membersLink?: string;
  dashboardLink?: string;
  description: string;
  isUserAdmin: boolean;
  isUserMember: boolean;
  isUserPending: boolean;
  organizationId?: string;
  hasOngoingMembership?: boolean;
  upcomingMembership?: OutgoingMembershipDto;
  associationDependentId?: string;
  isJoinBlocked?: boolean;
  blockedMessage?: string;
}

const MobileView = ({
  toggleShareModal,
  coverUrl,
  name,
  memberCount,
  memberPreview,
  address,
  description,
  manageText,
  isUserAdmin,
  isUserMember,
  isUserPending,
  join,
  editLink,
  dashboardLink,
  badgeUrl,
  membersLink,
  organizationId,
  hasOngoingMembership,
  associationDependentId,
  upcomingMembership,
  isJoinBlocked = false,
  blockedMessage
}: Props) => {
  const { t } = useTranslation(['organizations']);
  const flags = useFlags();
  const user = useSelector((state: any) => state.user?.user);
  const router = useRouter();
  const { query } = router;
  const isAssociationPage = query.associationId;
  const [orgActiveTab, setOrgActiveTab] = useState<'events' | 'team'>('events');
  const [assocActiveTab, setAssocActiveTab] = useState<'orgs' | 'events'>(
    'orgs'
  );
  return (
    <div>
      <div>
        <div
          className="bg-primary-200 w-full relative mb-4 bg-center bg-cover h-screen flex"
          style={{
            backgroundImage: `url(${coverUrl})`
          }}
        >
          <div
            className={classNames(
              'z-0 absolute left-0 right-0 bottom-0 top-[10%] flex items-end',
              styles.backgroundGradient
            )}
          />
          <div className="relative z-10 w-full flex mt-auto pb-4">
            <CenteredContainer>
              <div className="gap-4 flex flex-col">
                <Info
                  name={name}
                  memberCount={memberCount}
                  memberPreview={memberPreview}
                  address={address}
                  badgeUrl={badgeUrl}
                  membersLink={membersLink}
                  isUserAdmin={isUserAdmin}
                />
                <MainActions
                  toggleShareModal={toggleShareModal}
                  manageText={manageText}
                  isUserAdmin={isUserAdmin}
                  editLink={editLink}
                  isUserMember={isUserMember}
                  join={join}
                  isUserPending={isUserPending}
                  dashboardLink={dashboardLink}
                  hasOngoingMembership={hasOngoingMembership}
                  upcomingMembership={upcomingMembership}
                  isJoinBlocked={isJoinBlocked}
                  blockedMessage={blockedMessage}
                />
                <div className="text-base font-normal w-full pt-2">
                  {description}
                </div>

                {!isAssociationPage && associationDependentId && (
                  <AssociatedWith associationId={associationDependentId} />
                )}
              </div>
            </CenteredContainer>
          </div>
        </div>
      </div>

      {isAssociationPage && (
        <div className="px-5 mt-0">
          <div className="flex flex-row items-center justify-start gap-2">
            <PillButton
              active={assocActiveTab === 'orgs'}
              label={t('ourOrgs')}
              icon="flag"
              onClick={() => setAssocActiveTab('orgs')}
            />
            <PillButton
              active={assocActiveTab === 'events'}
              label={t('publicView.ourEvents')}
              icon="calendar"
              onClick={() => setAssocActiveTab('events')}
            />
          </div>
        </div>
      )}

      {isAssociationPage && (
        <>
          {assocActiveTab === 'orgs' && (
            <div className="py-6">
              <OrgDirectory associationId={organizationId} user={user} />
            </div>
          )}
          {assocActiveTab === 'events' && (
            <div className="py-6">
              <AssocEventsList associationId={organizationId} user={user} />
            </div>
          )}
        </>
      )}

      {!isAssociationPage &&
        (flags[FLAGS.ENABLE_ORG_EVENTS] ||
          flags[FLAGS.ENABLE_ORGANIZATION_TITLES]) && (
          <div className="w-full flex flex-col gap-2 justify-center text-base font-normal">
            <div className="ml-6">
              <div className="flex flex-row items-center justify-start gap-2">
                {flags[FLAGS.ENABLE_ORG_EVENTS] && (
                  <PillButton
                    active={orgActiveTab === 'events'}
                    onClick={() => setOrgActiveTab('events')}
                    label={t('publicView.ourEvents')}
                    icon="calendar"
                  />
                )}
                {flags[FLAGS.ENABLE_ORGANIZATION_TITLES] && (
                  <PillButton
                    active={orgActiveTab === 'team'}
                    onClick={() => setOrgActiveTab('team')}
                    label={t('publicView.ourTeam')}
                    icon="briefcase"
                  />
                )}
              </div>
            </div>
            {orgActiveTab === 'events' && (
              <OrgEventsList
                groupId={organizationId}
                organization={{
                  id: organizationId,
                  name,
                  badgeImageUrl: badgeUrl
                }}
                user={user}
              />
            )}
            {orgActiveTab === 'team' && (
              <TeamList organizationId={organizationId} user={user} />
            )}
          </div>
        )}
    </div>
  );
};

export default MobileView;
